import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { OutlookHealthPanel } from "../components/outlook/OutlookHealthPanel";
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, Clock, RefreshCw,
  ChevronDown, ChevronRight, Server, Database, ShieldCheck, Search, Loader2,
  DollarSign, Calendar, Info, Image as ImageIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  ok: { label: "Healthy", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", Icon: CheckCircle2 },
  stale: { label: "Stale", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", Icon: Clock },
  failed: { label: "Failed", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500", Icon: XCircle },
  running: { label: "Running", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500", Icon: Loader2 },
  pending: { label: "No data yet", cls: "bg-gray-50 text-gray-500 border-gray-200", dot: "bg-gray-300", Icon: Clock },
  missing: { label: "Never ran", cls: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500", Icon: AlertTriangle },
  suspended: { label: "Suspended", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-600", Icon: AlertTriangle },
  off: { label: "Off", cls: "bg-slate-50 text-slate-500 border-slate-200", dot: "bg-slate-300 ring-1 ring-slate-400", Icon: Clock },
  not_setup: { label: "Not set up", cls: "bg-slate-50 text-slate-400 border-slate-200", dot: "bg-white ring-1 ring-slate-300", Icon: Clock },
};

const frChip = (raw) => {
  if (raw === "complete" || raw === "complete_minor_gaps") return "ok";
  if (raw === "failed" || raw === "completed_with_errors" || raw === "interrupted") return "failed";
  if (raw === "running" || (raw || "").startsWith("retrying")) return "running";
  return "pending";
};

const DiagShot = ({ url, testid }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    if (src) { setSrc(null); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${url}`, { responseType: "blob" });
      setSrc(URL.createObjectURL(res.data));
    } catch (e) { /* no screenshot */ }
    finally { setLoading(false); }
  };
  return (
    <div className="mt-1">
      <button onClick={toggle} data-testid={testid}
        className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 hover:underline">
        {loading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <ImageIcon className="h-2.5 w-2.5" />}
        {loading ? "Loading…" : src ? "Hide screen" : "View screen Playwright saw"}
      </button>
      {src && <img src={src} alt="Gingr login screen" className="mt-1 max-h-72 rounded border border-gray-200 shadow-sm" />}
    </div>
  );
};

const StatusChip = ({ s }) => {
  const cfg = STATUS[s] || STATUS.pending;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cfg.cls}`} data-testid={`status-chip-${s}`}>
      <Icon className="h-3.5 w-3.5" /> {cfg.label}
    </span>
  );
};

const SummaryTile = ({ tone, count, label, Icon, testid }) => {
  const tones = {
    slate: "from-slate-800 to-slate-900 text-white",
    emerald: "from-emerald-500 to-emerald-600 text-white",
    amber: "from-amber-400 to-amber-500 text-white",
    rose: "from-rose-500 to-rose-600 text-white",
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tones[tone]} px-5 py-4 shadow-sm`} data-testid={testid}>
      <Icon className="absolute -right-3 -top-3 h-16 w-16 opacity-10" />
      <div className="text-4xl font-extrabold leading-none tracking-tight">{count}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide opacity-90">{label}</div>
    </div>
  );
};

function CommandCenter({ embedded = false }) {
  const [data, setData] = useState(null);
  const [cash, setCash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);
  const [openLoc, setOpenLoc] = useState({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async (isRefresh) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      const [r, c] = await Promise.all([
        axios.get(`${API}/admin/command-center/job-health`),
        axios.get(`${API}/admin/command-center/cash-health`),
      ]);
      setData(r.data);
      setCash(c.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load job health");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [load]);

  const jobs = data?.jobs || [];
  const counts = data?.counts || { total: 0, ok: 0, stale: 0, failed: 0, pending: 0, missing: 0 };
  const groupOrder = data?.group_order || ["Frequent (≤15 min)", "Hourly", "Nightly", "Weekly"];

  const visible = useMemo(() => jobs.filter((j) => {
    if (filter === "missing" ? !(j.locations || []).some((l) => l.status === "missing") : filter !== "all" && j.status !== filter) return false;
    if (q && !j.label.toLowerCase().includes(q.toLowerCase()) && !j.key.includes(q.toLowerCase())) return false;
    return true;
  }), [jobs, q, filter]);

  const grouped = useMemo(() => {
    const m = {};
    for (const j of visible) (m[j.group] = m[j.group] || []).push(j);
    return m;
  }, [visible]);

  return (
    <div className={embedded ? "" : "mx-auto max-w-6xl px-4 py-6"} data-testid="command-center">
      {/* Header band */}
      <div className={embedded ? "flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 [&_h1]:text-lg [&_h1]:text-slate-900 [&_p]:text-slate-500 [&_button]:bg-slate-100 [&_button]:text-slate-700 [&_.rounded-xl]:bg-teal-50 [&_.rounded-xl]:text-teal-700" : "rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 px-6 py-5 text-white shadow-md"}>
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-white/10 p-2.5"><Server className="h-6 w-6" /></div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">FourPaw Command Center</h1>
            <p className="text-sm text-white/70">Background job health across every location · super-admin only</p>
          </div>
          <button onClick={() => load(true)} disabled={refreshing}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 transition-colors hover:bg-white/20 disabled:opacity-50"
            data-testid="refresh-btn">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="mt-10 flex items-center justify-center gap-2 text-gray-500" data-testid="cc-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading job health…
        </div>
      ) : err ? (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="cc-error">
          <AlertTriangle className="h-4 w-4" /> {err}
        </div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryTile tone="slate" count={counts.total} label="Total jobs" Icon={Database} testid="tile-total" />
            <SummaryTile tone="emerald" count={counts.ok} label="Healthy" Icon={CheckCircle2} testid="tile-ok" />
            <SummaryTile tone="amber" count={counts.stale} label="Stale" Icon={Clock} testid="tile-stale" />
            <SummaryTile tone="rose" count={counts.failed} label="Failing" Icon={XCircle} testid="tile-failed" />
          </div>

          {/* Controls */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs…"
                className="h-9 w-56 rounded-lg border border-gray-200 pl-8 pr-3 text-sm focus:border-indigo-400 focus:outline-none"
                data-testid="job-search" />
            </div>
            <span className="hidden text-xs text-gray-400 sm:inline">Auto-refresh every 30s{counts.pending ? ` · ${counts.pending} awaiting first heartbeat` : ""}</span>
            <div className="ml-auto flex items-center gap-1 rounded-lg bg-gray-100 p-1 text-xs">
              {[["all", "All"], ["failed", "Failing"], ["stale", "Stale"], ["missing", "Missing locations"]].map(([v, l]) => (
                <button key={v} onClick={() => setFilter(v)}
                  className={`rounded-md px-3 py-1.5 font-medium transition-colors ${filter === v ? "bg-white text-indigo-700 shadow-sm" : "text-gray-600 hover:bg-gray-200"}`}
                  data-testid={`filter-${v}`}>{l}</button>
              ))}
            </div>
          </div>

          {/* Grouped job list */}
          <div className="mt-4 space-y-6">
            {groupOrder.filter((g) => grouped[g]?.length).map((g) => (
              <div key={g}>
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                  <Clock className="h-3.5 w-3.5" /> {g}
                  <span className="text-gray-300">·</span>
                  <span className="font-medium normal-case text-gray-400">{grouped[g].length} jobs</span>
                </div>
                <Card className="divide-y divide-gray-100 overflow-hidden">
                  {grouped[g].map((j) => {
                    const isOpen = openLoc[j.key];
                    const locs = j.locations || [];
                    const okLocs = locs.filter((l) => l.status === "ok").length;
                    const missLocs = locs.filter((l) => l.status === "missing").length;
                    return (
                      <div key={j.key} data-testid={`job-row-${j.key}`}>
                        <div className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/60">
                          <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${(STATUS[j.status] || STATUS.pending).dot}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-gray-900">{j.label}</span>
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-500">{j.cadence}</span>
                              {locs.length > 0 && (
                                <button onClick={() => setOpenLoc((s) => ({ ...s, [j.key]: !s[j.key] }))}
                                  className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200"
                                  data-testid={`toggle-loc-${j.key}`}>
                                  {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  {okLocs}/{locs.length} locations
                                </button>
                              )}
                              {missLocs > 0 && (
                                <span className="rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium text-orange-700" data-testid={`missing-badge-${j.key}`}>
                                  {missLocs} never ran
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-gray-500">
                              <span>Last run: <span className="font-mono text-gray-700">{j.last_run_ago || "—"}</span></span>
                              <span>Last success: <span className="font-mono text-gray-700">{j.last_success_ago || "—"}</span></span>
                            </div>
                            {j.error && (
                              <div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-rose-50 px-2 py-1 text-[11px] text-rose-700" data-testid={`job-error-${j.key}`}>
                                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                <span className="font-mono break-all">{j.error}</span>
                              </div>
                            )}
                            {isOpen && locs.length > 0 && (
                              <div className="mt-2 space-y-1 rounded-lg bg-slate-50 p-2" data-testid={`loc-breakdown-${j.key}`}>
                                {locs.map((l) => (
                                  <div key={l.location_id} className="rounded-md bg-white px-2 py-1 text-[11px]" data-testid={`loc-row-${j.key}-${l.location_id}`}>
                                    <div className="flex items-center gap-1.5">
                                      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${(STATUS[l.status] || STATUS.pending).dot}`} />
                                      <span className="truncate text-gray-700">{l.name}</span>
                                      <span className={`ml-auto ${l.status === "missing" ? "font-medium text-orange-700" : "text-gray-400"}`} data-testid={`loc-status-${j.key}-${l.location_id}`}>
                                        {l.never_ran ? l.note : (l.last_run_ago || l.status)}
                                      </span>
                                      {l.setup_path && (
                                        <a href={l.setup_path} className="rounded bg-indigo-50 px-1.5 py-0.5 font-medium text-indigo-700 hover:bg-indigo-100" data-testid={`loc-setup-${j.key}-${l.location_id}`}>Switch on</a>
                                      )}
                                    </div>
                                    {l.error && (
                                      <div className="mt-0.5 flex items-start gap-1 pl-3.5 text-[10px] text-rose-600" data-testid={`loc-error-${j.key}-${l.location_id}`}>
                                        <AlertTriangle className="mt-0.5 h-2.5 w-2.5 flex-shrink-0" />
                                        <span className="break-all font-mono">{l.error}</span>
                                      </div>
                                    )}
                                    {l.diagnostic && (l.diagnostic.message || l.diagnostic.screenshot_url) && (
                                      <div className="mt-0.5 pl-3.5" data-testid={`loc-diag-${j.key}-${l.location_id}`}>
                                        {l.diagnostic.message && !l.error && (
                                          <div className="text-[10px] text-gray-500 break-words">{l.diagnostic.message}</div>
                                        )}
                                        {l.diagnostic.screenshot_url && (
                                          <DiagShot url={l.diagnostic.screenshot_url} testid={`loc-shot-${j.key}-${l.location_id}`} />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0"><StatusChip s={j.status} /></div>
                        </div>
                      </div>
                    );
                  })}
                </Card>
              </div>
            ))}
            {visible.length === 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500" data-testid="cc-empty">
                No jobs match this filter.
              </div>
            )}
          </div>

          <CashRevenuePanel cash={cash} openLoc={openLoc} setOpenLoc={setOpenLoc} />
          <OutlookHealthPanel />

          <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-[11px] text-gray-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Super-admins only · updated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"} · P2 adds login/activity audit + usage metadata
          </p>
        </>
      )}
    </div>
  );
}

function CashRevenuePanel({ cash, openLoc, setOpenLoc }) {
  if (!cash) return null;
  const jobs = cash.jobs || [];
  const fr = cash.full_rebuild;
  const sch = cash.schedule || {};

  return (
    <div className="mt-8" data-testid="cash-revenue-panel">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
        <DollarSign className="h-3.5 w-3.5" /> Cash Revenue
        <span className="text-gray-300">·</span>
        <span className="font-medium normal-case text-gray-400">what runs every night & why</span>
      </div>

      {/* Schedule-state summary */}
      <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs" data-testid="cash-schedule-state">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-slate-500" />
            <span className="font-semibold text-slate-700">Weekly full rebuild:</span>
            {sch.weekly_enabled
              ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700">ON · every {sch.weekly_weekday_name} ~{sch.run_hour_et} AM ET</span>
              : <span className="rounded-full bg-gray-200 px-2 py-0.5 font-medium text-gray-600">OFF</span>}
          </span>
          {sch.run_tonight_armed && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700" data-testid="cash-run-tonight">⏱ Armed for tonight</span>
          )}
          <span className="text-slate-600">Rebuilds from <span className="font-mono">{sch.start_date}</span></span>
        </div>
        <p className="mt-2 flex items-start gap-1.5 text-[11px] italic text-slate-400">
          <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
          A full rebuild can also re-launch on its own after a deploy if one was interrupted mid-run — so if it appears "unprompted" off-hours, that's expected.
        </p>
      </div>

      <Card className="divide-y divide-gray-100 overflow-hidden">
        {jobs.map((j) => {
          const isFull = j.key === "cash_full_rebuild";
          const isOpen = openLoc[j.key];
          const locs = j.locations || [];
          const okLocs = locs.filter((l) => l.status === "ok").length;
          return (
            <div key={j.key} data-testid={`cash-job-${j.key}`}>
              <div className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/60">
                <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${(STATUS[j.status] || STATUS.pending).dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">{j.label}</span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-500">{j.cadence}</span>
                    {locs.length > 0 && (
                      <button onClick={() => setOpenLoc((s) => ({ ...s, [j.key]: !s[j.key] }))}
                        className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200"
                        data-testid={`cash-toggle-${j.key}`}>
                        {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {okLocs}/{locs.length} locations
                      </button>
                    )}
                  </div>
                  {j.note && <div className="mt-0.5 text-[11px] text-gray-400">{j.note}</div>}
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-gray-500">
                    <span>Last run: <span className="font-mono text-gray-700">{j.last_run_ago || "—"}</span></span>
                    <span>Last success: <span className="font-mono text-gray-700">{j.last_success_ago || "—"}</span></span>
                    {isFull && fr && (
                      <span>{fr.completed_locations ?? "?"}/{fr.total_locations ?? "?"} locations{fr.parallel_lanes ? ` · ${fr.parallel_lanes} lanes` : ""}</span>
                    )}
                  </div>
                  {j.error && (
                    <div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-rose-50 px-2 py-1 text-[11px] text-rose-700" data-testid={`cash-error-${j.key}`}>
                      <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                      <span className="font-mono break-all">{j.error}</span>
                    </div>
                  )}
                  {isOpen && locs.length > 0 && (
                    <div className="mt-2 space-y-1 rounded-lg bg-slate-50 p-2" data-testid={`cash-breakdown-${j.key}`}>
                      {(isFull && fr ? fr.locations : locs).map((l) => {
                        const locChip = isFull ? frChip(l.status) : l.status;
                        return (
                        <div key={l.location_id} className="flex items-center gap-2 rounded-md bg-white px-2 py-1 text-[11px]">
                          <span className={`h-2 w-2 flex-shrink-0 rounded-full ${(STATUS[locChip] || STATUS.pending).dot}`} />
                          <span className="w-40 truncate text-gray-700">{l.name}</span>
                          {isFull ? (
                            <span className="text-gray-500">
                              {l.started_ago ? `started ${l.started_ago}` : "—"}
                              {l.completed_ago ? ` → finished ${l.completed_ago}` : ""}
                              {typeof l.days_written === "number" ? ` · ${l.days_written} days` : ""}
                              {l.failed_windows ? <span className="text-rose-600"> · {l.failed_windows} failed windows</span> : ""}
                              {l.deposits_failed ? <span className="text-amber-600"> · deposits pending</span> : ""}
                            </span>
                          ) : (
                            <span className="ml-auto text-gray-400">{l.last_run_ago || l.status}</span>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0"><StatusChip s={j.status} /></div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export default CommandCenter;
